import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Spinner, Stack } from "react-bootstrap";
import type { Candidate, ScorecardInput } from "@ld-scorecard/shared";
import { useApi } from "../api";
import { useMe } from "../App";
import ScorecardForm from "../components/ScorecardForm";

const emptyDraft = (): ScorecardInput => ({
  signalScores: {},
  categoryScores: {},
  redFlags: {},
  categoryNotes: {},
  submitted: false,
});

export default function ScorecardPage() {
  const { id = "" } = useParams();
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [draft, setDraft] = useState<ScorecardInput>(emptyDraft());
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.getCandidate(id), api.getMyScorecard(id)])
      .then(([cand, card]) => {
        if (cancelled) return;
        setCandidate(cand);
        setSubmitted(card.submitted);
        setDraft({
          signalScores: card.signalScores ?? {},
          categoryScores: card.categoryScores ?? {},
          redFlags: card.redFlags ?? {},
          categoryNotes: card.categoryNotes ?? {},
          recommendation: card.recommendation,
          coachEveryone: card.coachEveryone,
          gapConcern: card.gapConcern,
          panelistName: card.panelistName ?? me?.name,
          submitted: card.submitted,
        });
      })
      .catch((e) => !cancelled && setError(String(e.message ?? e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);

  const save = async (asSubmit: boolean) => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const saved = await api.saveScorecard(id, { ...draft, submitted: asSubmit });
      setSubmitted(saved.submitted);
      if (asSubmit) {
        navigate(`/candidates/${id}/results`);
      } else {
        setStatus("Draft saved.");
      }
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner animation="border" />;
  if (error && !candidate) return <Alert variant="danger">{error}</Alert>;

  return (
    <>
      <Stack direction="horizontal" gap={2} className="mb-3 flex-wrap">
        <Link to={candidate ? `/requisitions/${candidate.reqId}` : "/"} className="btn btn-link px-0">
          ← Requisition
        </Link>
        <h3 className="mb-0 ms-2">{candidate?.name}</h3>
        {submitted ? (
          <Badge bg="success">Submitted</Badge>
        ) : (
          <Badge bg="secondary">Draft</Badge>
        )}
      </Stack>

      {submitted && (
        <Alert variant="info" className="py-2">
          You've submitted this scorecard. You can still edit and re-submit, or{" "}
          <Link to={`/candidates/${id}/results`}>view the panel results</Link>.
        </Alert>
      )}

      <ScorecardForm draft={draft} setDraft={setDraft} />

      {error && <Alert variant="danger">{error}</Alert>}
      {status && <Alert variant="success" className="py-2">{status}</Alert>}

      <div className="action-bar d-flex justify-content-end gap-2 py-3 sticky-bottom">
        <Button variant="outline-secondary" disabled={busy} onClick={() => void save(false)}>
          Save draft
        </Button>
        <Button variant="primary" disabled={busy} onClick={() => void save(true)}>
          {submitted ? "Re-submit" : "Submit scorecard"}
        </Button>
      </div>
    </>
  );
}
